import React, { useEffect, useState } from "react";
import {
  Button,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import APIHelper from "../utils/APIHelper";

export default function WhatsAppSettingsPage({ token }) {
  const [form, setForm] = useState({
    wabaId: "",
    phoneNumberId: "",
    businessId: "",
    businessToken: "",
    senderDisplayName: "",
    defaultTemplate: "",
    webhookVerifyToken: "",
    enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  // Default to false for testing phase - ignore opt-in check
  // TODO: Change default to true for production compliance
  const [onlyOptedIn, setOnlyOptedIn] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const cfg = await APIHelper.getWhatsAppConfig(token);
        if (cfg) {
          setForm((f) => ({
            ...f,
            ...cfg,
            businessToken: "", // never populate token back into UI
          }));
        }
        const tpls = await APIHelper.getWhatsAppTemplates(token);
        setTemplates(tpls);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const save = async () => {
    try {
      setMessage("");
      setError("");
      await APIHelper.updateWhatsAppConfig(token, form);
      setMessage("Saved successfully.");
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
  };

  const testSend = async () => {
    try {
      setMessage("");
      setError("");
      const to = prompt(
        "Enter a WhatsApp number in E.164 (e.g., +15551234567)"
      );
      if (!to) return;
      const selected =
        templates.find((t) => t.name === form.defaultTemplate) ||
        templates?.[0];
      const name = selected?.name || "";
      if (!name) {
        setError("No approved templates available. Create/approve one first.");
        return;
      }
      const languageCode = selected?.language || "en_US";
      await APIHelper.sendWhatsAppTest(token, {
        to,
        template: { name, languageCode, variables: {} },
      });
      setMessage("Test message sent.");
    } catch (e) {
      const data = e?.response?.data;
      const errMsg =
        data?.error ||
        data?.message ||
        data?.message?.error?.message ||
        e?.message ||
        "Failed to send";
      setError(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
    }
  };

  const handleBroadcast = async () => {
    try {
      setBroadcasting(true);
      setMessage("");
      setError("");

      const selected =
        templates.find((t) => t.name === form.defaultTemplate) ||
        templates?.[0];
      const name = selected?.name || "";
      if (!name) {
        setError("No approved templates available. Create/approve one first.");
        setBroadcastDialogOpen(false);
        setBroadcasting(false);
        return;
      }
      const languageCode = selected?.language || "en_US";

      const result = await APIHelper.broadcastWhatsApp(token, {
        template: { name, languageCode, variables: {} },
        onlyOptedIn,
      });

      setBroadcastDialogOpen(false);
      setBroadcasting(false);

      if (result.success) {
        setMessage(
          `Broadcast completed! Sent: ${result.sent}, Failed: ${result.failed}, Total: ${result.total}`
        );
        if (result.errors && result.errors.length > 0) {
          console.warn("Broadcast errors:", result.errors);
        }
      } else {
        setError(result.error || "Broadcast failed");
      }
    } catch (e) {
      setBroadcasting(false);
      const data = e?.response?.data;
      const errMsg =
        data?.error ||
        data?.message ||
        data?.message?.error?.message ||
        e?.message ||
        "Failed to broadcast";
      setError(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
      setBroadcastDialogOpen(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <WhatsAppIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
          WhatsApp Settings
        </Typography>
      </div>

      {message && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-green-800 dark:text-green-200">
              ✅ {message}
            </Typography>
            <button
              onClick={() => setMessage("")}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {error}
            </Typography>
            <button
              onClick={() => setError("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      <div className="card-modern p-6">
        <Stack spacing={3}>
          <TextField
            label="WABA ID"
            value={form.wabaId}
            onChange={(e) => setForm({ ...form, wabaId: e.target.value })}
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Phone Number ID"
            value={form.phoneNumberId}
            onChange={(e) =>
              setForm({ ...form, phoneNumberId: e.target.value })
            }
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Business ID (optional)"
            value={form.businessId}
            onChange={(e) => setForm({ ...form, businessId: e.target.value })}
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Business Token"
            value={form.businessToken}
            onChange={(e) =>
              setForm({ ...form, businessToken: e.target.value })
            }
            type="password"
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Sender Display Name"
            value={form.senderDisplayName}
            onChange={(e) =>
              setForm({ ...form, senderDisplayName: e.target.value })
            }
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />

          <FormControl fullWidth size="small">
            <InputLabel id="wa-default-template-label" className="text-gray-700 dark:text-gray-300">
              Default Template
            </InputLabel>
            <Select
              labelId="wa-default-template-label"
              label="Default Template"
              value={form.defaultTemplate}
              onChange={(e) =>
                setForm({ ...form, defaultTemplate: e.target.value })
              }
              className="bg-white dark:bg-gray-800"
            >
              {templates.map((t) => (
                <MenuItem key={t.id || t.name} value={t.name}>
                  {t.name} ({t.language || ""})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Webhook Verify Token"
            value={form.webhookVerifyToken}
            onChange={(e) =>
              setForm({ ...form, webhookVerifyToken: e.target.value })
            }
            className="bg-white dark:bg-gray-800"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              variant="contained"
              onClick={save}
              disabled={loading}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={testSend}
              disabled={loading}
              className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
            >
              Send Test
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setBroadcastDialogOpen(true)}
              disabled={loading || !form.defaultTemplate}
              className="bg-secondary-600 hover:bg-secondary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
            >
              Broadcast
            </Button>
          </div>
        </Stack>
      </div>

      {/* Broadcast Confirmation Dialog */}
      <Dialog
        open={broadcastDialogOpen}
        onClose={() => !broadcasting && setBroadcastDialogOpen(false)}
        PaperProps={{
          className: "bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
        }}
      >
        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <span className="text-xl">📢</span>
            </div>
            <span>Broadcast WhatsApp Template</span>
          </div>
        </DialogTitle>
        <DialogContent className="px-6 py-6">
          <DialogContentText className="text-gray-700 dark:text-gray-300 mb-4">
            This will send the selected template to customers with phone
            numbers.
            {onlyOptedIn
              ? " Only customers who have opted in will receive the message."
              : " Currently in testing mode: sending to ALL customers with phone numbers."}
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyOptedIn}
                onChange={(e) => setOnlyOptedIn(e.target.checked)}
                disabled={broadcasting}
                className="text-primary-600 dark:text-primary-400"
              />
            }
            label="Only send to customers who have opted in"
            className="text-gray-700 dark:text-gray-300 mb-4"
          />
          {!onlyOptedIn && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl mb-4">
              <Typography className="text-sm text-blue-800 dark:text-blue-200">
                ℹ️ Testing mode: Opt-in check is disabled. Enable the checkbox above
                to respect opt-in status for production use.
              </Typography>
            </div>
          )}
          {broadcasting && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <CircularProgress size={20} />
              <Typography variant="body2" className="text-gray-700 dark:text-gray-300">
                Broadcasting messages... This may take a while.
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions className="px-6 pb-6 pt-2">
          <Button
            onClick={() => setBroadcastDialogOpen(false)}
            disabled={broadcasting}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBroadcast}
            variant="contained"
            color="secondary"
            disabled={broadcasting}
            className="bg-secondary-600 hover:bg-secondary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            {broadcasting ? "Broadcasting..." : "Broadcast"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
